// 단어추리왕 사전 로직.
//
// 두 개의 목록을 합쳐서 쓴다 (원조 Wordle 과 같은 구조):
//   answers.js    정답으로 출제되는 쉬운 단어 — 이것만 정답 풀이 된다
//   dictionary.js 시도만 허용되는 단어 — 정답으로는 나오지 않는다
// 정답 단어도 당연히 시도할 수 있어야 하므로 허용 사전에 자동 포함된다.
//
// dictionary.js 는 6만 단어(약 210KB)라 메인 번들에 넣으면 첫 로딩이 느려진다.
// 그래서 동적 import 로 별도 청크로 분리하고, 게임에 들어올 때만 받아온다.
// 정답은 answers.js 에서 뽑으므로 사전이 아직 안 와도 게임은 바로 시작된다.
//
// 자모 길이는 decomposeToJamo 기준이며 4~6칸만 사용한다.
//   예) 커피 → 4칸, 사랑 → 5칸, 무지개 → 6칸

import { decomposeToJamo, composeCandidates } from './hangul';
import { ANSWER_WORDS } from './answers';

export const MIN_LENGTH = 4;
export const MAX_LENGTH = 6;

const inRange = (length) => length >= MIN_LENGTH && length <= MAX_LENGTH;

// 정답 풀: 길이별로 나눠 둔다
const answerBuckets = { 4: [], 5: [], 6: [] };
// 허용 사전: 정답 + (나중에 로드되는) 추가 단어
const dictionary = new Set();

for (const word of ANSWER_WORDS) {
  const jamo = decomposeToJamo(word);
  if (!jamo || !inRange(jamo.length)) continue;
  if (dictionary.has(word)) continue; // 중복 제거
  answerBuckets[jamo.length].push(word);
  dictionary.add(word);
}

// ── 사전 지연 로딩 ───────────────────────────────────────
let dictionaryPromise = null;
let isDictionaryReady = false;

/**
 * 허용 사전을 불러온다. 여러 번 불러도 실제 요청은 한 번만 나간다.
 * 실패해도 게임은 계속 돌아가야 하므로 조용히 넘어간다
 * (그 경우 정답 목록 479개만으로 검증한다).
 */
export const loadDictionary = () => {
  if (dictionaryPromise) return dictionaryPromise;

  dictionaryPromise = import(
    /* webpackChunkName: "wordle-dictionary" */ './dictionary'
  )
    .then(({ EXTRA_WORDS }) => {
      for (const word of EXTRA_WORDS) {
        const jamo = decomposeToJamo(word);
        if (!jamo || !inRange(jamo.length)) continue;
        dictionary.add(word);
      }
      isDictionaryReady = true;
    })
    .catch(() => {
      // 네트워크 문제 등으로 실패 — 정답 목록만으로 진행한다
      isDictionaryReady = true;
    });

  return dictionaryPromise;
};

/** 사전 로딩이 끝났는지 */
export const isDictionaryLoaded = () => isDictionaryReady;

/** 해당 자모 길이의 정답 후보 목록 */
export const getWordsByLength = (length) => answerBuckets[length] || [];

/** 시도 가능한 단어인지 (정답 풀 + 추가 사전) */
export const isInDictionary = (word) => dictionary.has(word);

/** 해당 길이에서 무작위 정답 하나 */
export const getRandomWord = (length) => {
  const pool = getWordsByLength(length);
  if (pool.length === 0) return null;
  return pool[Math.floor(Math.random() * pool.length)];
};

/**
 * 입력한 자모 배열이 사전에 있는 단어인지 확인한다.
 * 완전 분해는 중의적이므로(ㅎㅏㄱㄱㅛ → 학교/하꾜) 가능한 조합을 모두
 * 만들어 보고 하나라도 사전에 있으면 통과시킨다.
 * @returns 매칭된 단어 또는 null
 */
export const lookupByJamo = (jamoList) => {
  for (const candidate of composeCandidates(jamoList)) {
    if (dictionary.has(candidate)) return candidate;
  }
  return null;
};

/** 사전 규모 확인용 (검증 스크립트에서 사용) */
export const getStats = () => ({
  answers: {
    4: answerBuckets[4].length,
    5: answerBuckets[5].length,
    6: answerBuckets[6].length,
    total: answerBuckets[4].length + answerBuckets[5].length + answerBuckets[6].length
  },
  dictionary: dictionary.size
});
